import { formatDuration, intervalToDuration } from 'date-fns';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';

import { HStack, Text } from '@chakra-ui/react';

import { NumberInput } from '../number-input/number-input';

export type DurationUnit = 'days' | 'hours' | 'minutes' | 'seconds';

export interface DurationInputProps {
  /**
   * The current value in the specified unit
   */
  value?: number;

  /**
   * Callback fired when the value changes
   * @param value - The new value in the specified unit
   * @param unit - The unit of the value
   */
  onChange?: (value: number | undefined, unit: DurationUnit) => void;

  /**
   * The unit for the input value and onChange callback
   */
  unit?: DurationUnit;

  /**
   * List of available units to display as inputs
   */
  availableUnits?: DurationUnit[];

  /**
   * Whether the inputs are disabled
   */
  isDisabled?: boolean;

  /**
   * Whether the inputs are read-only
   */
  isReadOnly?: boolean;

  /**
   * Whether the inputs are invalid
   */
  isInvalid?: boolean;
}

// Unit-specific constraints for logical ranges
const UNIT_CONSTRAINTS: Record<DurationUnit, { min: number; max: number }> = {
  days: { min: 0, max: Infinity }, // no limit to days
  hours: { min: 0, max: 23 }, // 0-23 hours in a day
  minutes: { min: 0, max: 59 }, // 0-59 minutes in an hour
  seconds: { min: 0, max: 59 }, // 0-59 seconds in a minute
};

// Conversion factors from each unit to seconds
const UNIT_TO_SECONDS: Record<DurationUnit, number> = {
  days: 24 * 60 * 60,
  hours: 60 * 60,
  minutes: 60,
  seconds: 1,
};

// Helper to convert a value and unit to total seconds for easier calculations
const convertToSeconds = (value: number, unit: DurationUnit): number => {
  return value * UNIT_TO_SECONDS[unit];
};

// Helper to break down total seconds into component units
const breakdownDuration = (
  totalSeconds: number,
  units: DurationUnit[],
): Partial<Record<DurationUnit, number>> => {
  // Sort units by size (largest first) for proper breakdown
  const unitOrder: DurationUnit[] = ['days', 'hours', 'minutes', 'seconds'];
  const sortedUnits = unitOrder.filter((unit) => units.includes(unit));
  const breakdown: Partial<Record<DurationUnit, number>> = {};

  let remaining = Math.round(totalSeconds); // Round to avoid floating point issues

  for (const unitKey of sortedUnits) {
    const unitInSeconds = UNIT_TO_SECONDS[unitKey];
    breakdown[unitKey] = Math.floor(remaining / unitInSeconds);
    remaining = remaining % unitInSeconds;
  }

  return breakdown;
};

// Helper to format a duration value using date-fns for human-readable display
export const formatDurationForUnit = (
  value: number,
  unit: DurationUnit,
): string => {
  // Convert to a duration object for date-fns
  const totalSeconds = convertToSeconds(value, unit);
  const duration = intervalToDuration({ start: 0, end: totalSeconds * 1000 });

  const formatted = formatDuration(duration, {
    delimiter: ', ',
  });

  return formatted || `0 ${unit}`;
};

/**
 * A duration input component that allows users to input time durations
 * with configurable units (days, hours, minutes, seconds).
 * The component can display multiple unit inputs and converts between units
 * to provide the value in the specified unit via the onChange callback.
 */
export const DurationInput = forwardRef<HTMLDivElement, DurationInputProps>(
  (
    {
      value = 0,
      onChange,
      unit = 'minutes',
      availableUnits = ['minutes'],
      isDisabled = false,
      isReadOnly = false,
      isInvalid = false,
    },
    ref,
  ): JSX.Element => {
    const totalSeconds = useMemo(() => {
      return convertToSeconds(value || 0, unit);
    }, [value, unit]);

    const calculatedUnitValues = useMemo(() => {
      return breakdownDuration(totalSeconds, availableUnits);
    }, [totalSeconds, availableUnits]);

    // Local state to track individual unit values for immediate clamping feedback
    const [localUnitValues, setLocalUnitValues] =
      useState(calculatedUnitValues);

    // Track when values are clamped to force NumberInput re-render
    const [inputKeys, setInputKeys] = useState<Record<DurationUnit, number>>(
      () =>
        Object.fromEntries(availableUnits.map((unit) => [unit, 0])) as Record<
          DurationUnit,
          number
        >,
    );

    // Validate props
    if (availableUnits.length === 0) {
      throw new Error('DurationInput: availableUnits array cannot be empty');
    }
    if (!availableUnits.includes(unit)) {
      throw new Error(
        `DurationInput: unit "${unit}" must be included in availableUnits array`,
      );
    }

    // Sync local state with calculated values when external value changes
    useEffect(() => {
      setLocalUnitValues(calculatedUnitValues);
    }, [calculatedUnitValues]);

    const handleUnitChange = useCallback(
      (unitKey: DurationUnit, newValue: number | string | null) => {
        if (!onChange) return;

        // Convert to number, defaulting to 0 for invalid/empty values
        const numericValue =
          typeof newValue === 'number' && !isNaN(newValue) ? newValue : 0;

        // Clamp to unit constraints
        const { min, max } = UNIT_CONSTRAINTS[unitKey];
        const clampedValue = Math.max(min, Math.min(max, numericValue));
        const wasValueClamped = clampedValue !== numericValue;

        // If value was clamped, increment the key to force NumberInput re-render
        if (wasValueClamped) {
          setInputKeys((prev) => ({
            ...prev,
            [unitKey]: prev[unitKey] + 1,
          }));
        }

        // Update local state with clamped value
        setLocalUnitValues((prev) => {
          const updatedValues = { ...prev, [unitKey]: clampedValue };

          // Calculate total seconds from all unit values
          const totalSeconds =
            (updatedValues.days || 0) * UNIT_TO_SECONDS.days +
            (updatedValues.hours || 0) * UNIT_TO_SECONDS.hours +
            (updatedValues.minutes || 0) * UNIT_TO_SECONDS.minutes +
            (updatedValues.seconds || 0) * UNIT_TO_SECONDS.seconds;

          // Convert back to the specified unit for the onChange callback
          const newValueInUnit = totalSeconds / UNIT_TO_SECONDS[unit];
          onChange(newValueInUnit, unit);

          return updatedValues;
        });
      },
      [onChange, unit],
    );

    return (
      <HStack ref={ref} spacing="2" alignItems="center">
        {availableUnits.map((unitKey) => {
          const constraints = UNIT_CONSTRAINTS[unitKey];
          return (
            <HStack
              key={unitKey}
              spacing="0"
              alignItems="center"
              justify="flex-start"
            >
              <NumberInput
                key={`${unitKey}-${inputKeys[unitKey]}`}
                value={localUnitValues[unitKey]}
                onChange={(newValue) => handleUnitChange(unitKey, newValue)}
                aria-label={`${unitKey} input`}
                min={constraints.min}
                max={constraints.max}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                isInvalid={isInvalid}
                format="number"
                integerOnly
                maxWidth="80px"
              />

              <Text
                color="gray.700"
                fontSize="md"
                noOfLines={1}
                width="90px"
                ml="-4"
              >
                {unitKey}
              </Text>
            </HStack>
          );
        })}
      </HStack>
    );
  },
);

DurationInput.displayName = 'DurationInput';
